import { Link, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { ArtworkCard } from '@/components/ArtworkCard'
import { EmptyState, ErrorState, SkeletonGrid } from '@/components/states'
import { paths } from '@/lib/paths'
import { useArtworks, useCategories } from '@/lib/queries'
import { breadcrumbSchema, canonicalURL, useSeo } from '@/lib/seo'
import { useLocale } from '@/lib/useLocale'

export function GalleryPage() {
  const locale = useLocale()
  const { t } = useTranslation()
  const { category: categorySlug } = useParams()

  const categories = useCategories(locale)
  const artworks = useArtworks(locale, categorySlug)

  const activeCategory = categories.data?.find((c) => c.slug === categorySlug)
  const items = artworks.data?.pages.flatMap((page) => page.items) ?? []
  const total = artworks.data?.pages[0]?.total ?? 0

  const title = activeCategory ? activeCategory.title : t('gallery.title')
  const description = activeCategory?.description || t('gallery.subtitle')
  const path = categorySlug ? `/gallery/${categorySlug}` : '/gallery'

  useSeo({
    title: `${title} — ${t('site.name')}`,
    description,
    path,
    locale,
    image: items[0]?.cover?.variants.find((v) => v.label === 'full')?.url,
    imageAlt: items[0]?.title,
    structuredData: [
      breadcrumbSchema([
        { name: t('nav.home'), url: canonicalURL(locale, '/') },
        { name: t('gallery.title'), url: canonicalURL(locale, '/gallery') },
        ...(activeCategory
          ? [{ name: activeCategory.title, url: canonicalURL(locale, path) }]
          : []),
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description,
        url: canonicalURL(locale, path),
        inLanguage: locale,
      },
    ],
  })

  return (
    <div className="container-page py-14">
      <header className="mb-10">
        <h1 className="text-4xl sm:text-5xl">{title}</h1>
        {description && <p className="mt-3 max-w-2xl text-stone-600">{description}</p>}
      </header>

      {/* Category filter. Links rather than buttons, so each filtered view has
          its own indexable URL and can be shared. */}
      <nav aria-label={t('gallery.filterLabel')} className="mb-10 flex flex-wrap gap-2">
        <FilterLink to={paths.gallery(locale)} active={!categorySlug}>
          {t('gallery.all')}
        </FilterLink>
        {categories.data?.map((category) => (
          <FilterLink
            key={category.id}
            to={paths.category(locale, category.slug)}
            active={category.slug === categorySlug}
          >
            {category.title}
          </FilterLink>
        ))}
      </nav>

      {artworks.isPending && <SkeletonGrid count={9} />}

      {artworks.isError && <ErrorState onRetry={() => void artworks.refetch()} />}

      {artworks.isSuccess && items.length === 0 && (
        <EmptyState message={categorySlug ? t('gallery.empty') : t('gallery.emptyAll')} />
      )}

      {items.length > 0 && (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((artwork, index) => (
              <ArtworkCard
                key={artwork.id}
                artwork={artwork}
                locale={locale}
                priority={index < 3}
              />
            ))}
          </div>

          <div className="mt-14 flex flex-col items-center gap-3">
            <p className="text-sm text-stone-400" aria-live="polite">
              {t('gallery.showing', { shown: items.length, total })}
            </p>
            {artworks.hasNextPage && (
              <button
                type="button"
                onClick={() => void artworks.fetchNextPage()}
                disabled={artworks.isFetchingNextPage}
                className="btn btn-secondary"
              >
                {artworks.isFetchingNextPage ? t('gallery.loading') : t('gallery.loadMore')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function FilterLink({
  to,
  active,
  children,
}: {
  to: string
  active: boolean
  children: React.ReactNode
}) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
        active
          ? 'border-stone-900 bg-stone-900 text-stone-50'
          : 'border-stone-300 text-stone-600 hover:border-stone-900 hover:text-stone-900'
      }`}
    >
      {children}
    </Link>
  )
}
